import { useState } from "react";
import { ImagePlus, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

export const inputCls = "w-full h-11 rounded-lg border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8] bg-white";
export const areaCls = "w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8]";
export const labelCls = "text-xs font-semibold text-slate-600 block mb-1";

export function Field({ label, children }) {
  return <label className="block"><span className={labelCls}>{label}</span>{children}</label>;
}

export async function uploadSiteImage(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/site/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data.url;
}

export function ImageField({ value, onChange, testid, label = "Image" }) {
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true);
    try {
      onChange(await uploadSiteImage(f));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-testid={testid}>
      <span className={labelCls}>{label}</span>
      <div className="flex items-center gap-3">
        {value ? (
          <img src={value} alt="" className="h-20 w-32 object-cover rounded-lg border border-slate-200" />
        ) : (
          <div className="h-20 w-32 grid place-items-center rounded-lg border border-dashed border-slate-300 text-[10px] text-slate-400">No image</div>
        )}
        <div className="flex flex-col gap-2">
          <label className="h-9 px-3 rounded-lg border border-slate-300 text-[#005EB8] hover:border-[#005EB8] text-xs font-bold inline-flex items-center gap-1.5 cursor-pointer w-fit" data-testid={`${testid}-upload`}>
            <input type="file" accept="image/*" className="hidden" onChange={pick} />
            <ImagePlus className="h-3.5 w-3.5" /> {busy ? "Uploading…" : value ? "Replace" : "Upload"}
          </label>
          {value && (
            <button type="button" onClick={() => onChange("")} className="text-xs text-[#D5281B] font-bold text-left" data-testid={`${testid}-remove`}>
              Remove
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export function ImageListField({ images = [], onChange, testid, label = "Images" }) {
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true);
    try {
      const url = await uploadSiteImage(f);
      onChange([...images, url]);
      toast.success("Image added");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-testid={testid}>
      <span className={labelCls}>{label}</span>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {(images || []).map((src, i) => (
          <div key={`${src}-${i}`} className="relative rounded-xl overflow-hidden border border-slate-200" data-testid={`${testid}-img-${i}`}>
            <img src={src} alt="" className="w-full h-24 object-cover" />
            <button type="button" onClick={() => onChange(images.filter((_, j) => j !== i))} aria-label="Remove image" data-testid={`${testid}-del-${i}`}
              className="absolute top-1.5 right-1.5 h-7 w-7 grid place-items-center rounded-lg bg-white/90 text-[#D5281B] border border-red-200 hover:bg-red-50">
              <Trash2 className="h-3 w-3" />
            </button>
          </div>
        ))}
        <label className="h-24 rounded-xl border-2 border-dashed border-slate-300 hover:border-[#005EB8] grid place-items-center cursor-pointer text-[#005EB8] transition-colors" data-testid={`${testid}-upload`}>
          <input type="file" accept="image/*" className="hidden" onChange={pick} />
          <span className="inline-flex flex-col items-center gap-1 text-[10px] font-bold">
            <ImagePlus className="h-4 w-4" /> {busy ? "Uploading…" : "Add image"}
          </span>
        </label>
      </div>
    </div>
  );
}

export function StringListEditor({ items = [], onChange, testid, label = "Items", placeholder = "Item text", multiline = false }) {
  const upd = (i, v) => onChange(items.map((x, j) => (j === i ? v : x)));
  return (
    <div data-testid={testid}>
      <span className={labelCls}>{label}</span>
      <div className="space-y-2">
        {(items || []).map((x, i) => (
          <div key={i} className="flex gap-2 items-start">
            {multiline ? (
              <textarea rows={2} className={areaCls} value={x} placeholder={placeholder} onChange={(e) => upd(i, e.target.value)} data-testid={`${testid}-item-${i}`} />
            ) : (
              <input className={inputCls} value={x} placeholder={placeholder} onChange={(e) => upd(i, e.target.value)} data-testid={`${testid}-item-${i}`} />
            )}
            <button type="button" onClick={() => onChange(items.filter((_, j) => j !== i))} aria-label="Remove" data-testid={`${testid}-del-${i}`}
              className="h-11 w-10 grid place-items-center rounded-lg border border-red-200 text-[#D5281B] hover:bg-red-50 shrink-0">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
      <button type="button" onClick={() => onChange([...(items || []), ""])}
        className="mt-2 h-9 px-3 rounded-lg border border-slate-300 text-[#005EB8] hover:border-[#005EB8] text-xs font-bold inline-flex items-center gap-1.5" data-testid={`${testid}-add`}>
        <Plus className="h-3.5 w-3.5" /> Add
      </button>
    </div>
  );
}

const BTN_STYLES = [["primary", "Primary (teal)"], ["outline", "Outline"], ["dark", "Dark"], ["light", "Light"], ["white", "White"]];

export function ButtonsEditor({ buttons = [], onChange, testid, label = "Buttons" }) {
  const upd = (i, k, v) => onChange(buttons.map((b, j) => (j === i ? { ...b, [k]: v } : b)));
  return (
    <div data-testid={testid}>
      <span className={labelCls}>{label}</span>
      <div className="space-y-2">
        {(buttons || []).map((b, i) => (
          <div key={i} className="grid sm:grid-cols-[1fr_1fr_130px_40px] grid-cols-2 gap-2 items-center">
            <input className={inputCls} placeholder="Button text" value={b.label || ""} onChange={(e) => upd(i, "label", e.target.value)} data-testid={`${testid}-label-${i}`} />
            <input className={inputCls} placeholder="Links to (e.g. /intake)" value={b.to || ""} onChange={(e) => upd(i, "to", e.target.value)} data-testid={`${testid}-to-${i}`} />
            <select className={inputCls} value={b.style || "primary"} onChange={(e) => upd(i, "style", e.target.value)} data-testid={`${testid}-style-${i}`}>
              {BTN_STYLES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <button type="button" onClick={() => onChange(buttons.filter((_, j) => j !== i))} aria-label="Remove button" data-testid={`${testid}-del-${i}`}
              className="h-11 w-10 grid place-items-center rounded-lg border border-red-200 text-[#D5281B] hover:bg-red-50">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
      <button type="button" onClick={() => onChange([...(buttons || []), { label: "", to: "/intake", style: "primary" }])}
        className="mt-2 h-9 px-3 rounded-lg border border-slate-300 text-[#005EB8] hover:border-[#005EB8] text-xs font-bold inline-flex items-center gap-1.5" data-testid={`${testid}-add`}>
        <Plus className="h-3.5 w-3.5" /> Add button
      </button>
    </div>
  );
}
